import { BookingActionContext, ValidationResult } from './bookingActionService';

export type MeetingTypeEnum = 'individual' | 'collective' | 'round_robin' | 'event';
export type ActionType = 'reschedule' | 'cancel';

export interface ActionConstraints {
  allowReschedule: boolean;
  allowCancel: boolean;
  rescheduleDeadlineHours?: number; // How many hours before meeting to stop allowing reschedules
  maxReschedules?: number; // Maximum number of reschedules allowed
  requiresHostApproval?: boolean; // For group meetings where host approval might be needed
  affectsOtherAttendees?: boolean; // For group meetings where action affects multiple people
  canChangeHost?: boolean; // For round-robin where reschedule might require different host
  notificationScope: 'guest' | 'host' | 'all'; // Who gets notified of the action
}

/**
 * Base action handler with common logic
 */
abstract class BaseActionHandler {
  abstract getActionConstraints(): ActionConstraints;
  
  /**
   * Validate if an action is allowed for this meeting type
   */
  validateAction(context: BookingActionContext, action: ActionType): ValidationResult {
    const constraints = this.getActionConstraints();
    const now = new Date();
    
    // Check if action is allowed for this meeting type
    if (action === 'reschedule' && !constraints.allowReschedule) {
      return {
        isValid: false,
        message: 'Rescheduling is not allowed for this meeting type.',
        canReschedule: false,
        canCancel: constraints.allowCancel,
      };
    }
    
    if (action === 'cancel' && !constraints.allowCancel) {
      return {
        isValid: false,
        message: 'Cancellation is not allowed for this meeting type.',
        canReschedule: constraints.allowReschedule,
        canCancel: false,
      };
    }
    
    // Check reschedule deadline
    if (action === 'reschedule' && constraints.rescheduleDeadlineHours) {
      const deadlineTime = new Date(context.startTime.getTime() - (constraints.rescheduleDeadlineHours * 60 * 60 * 1000));
      if (now > deadlineTime) {
        return {
          isValid: false,
          message: `Rescheduling is not allowed within ${constraints.rescheduleDeadlineHours} hours of the meeting.`,
          canReschedule: false,
          canCancel: constraints.allowCancel,
        };
      }
    }
    
    return {
      isValid: true,
      canReschedule: constraints.allowReschedule,
      canCancel: constraints.allowCancel,
    };
  }
  
  /**
   * Get user-friendly message about action impacts
   */
  getActionImpactMessage(action: ActionType): string {
    const constraints = this.getActionConstraints();
    
    if (action === 'reschedule') {
      if (constraints.affectsOtherAttendees) {
        return 'Rescheduling this meeting will affect all attendees. They will be notified of the change.';
      }
      if (constraints.requiresHostApproval) {
        return 'Your reschedule request will be sent to the host for approval.';
      }
      return 'You can reschedule this meeting to a new time.';
    }
    
    if (action === 'cancel') {
      if (constraints.affectsOtherAttendees) {
        return 'Canceling this meeting will notify all attendees and the host.';
      }
      return ''; // No message for individual cancel actions
    }
    
    return '';
  }
}

/**
 * Individual meeting action handler (1-on-1 meetings)
 */
class IndividualActionHandler extends BaseActionHandler {
  getActionConstraints(): ActionConstraints {
    return {
      allowReschedule: true,
      allowCancel: true,
      rescheduleDeadlineHours: undefined, // No deadline restriction for individual meetings
      maxReschedules: undefined, // No limit on reschedules
      requiresHostApproval: false,
      affectsOtherAttendees: false,
      canChangeHost: false,
      notificationScope: 'all', // Notify both guest and host
    };
  }
}

/**
 * Collective meeting action handler (multiple hosts, one-on-one with guest)
 */
class CollectiveActionHandler extends BaseActionHandler {
  getActionConstraints(): ActionConstraints {
    return {
      allowReschedule: true,
      allowCancel: true,
      rescheduleDeadlineHours: undefined,
      maxReschedules: undefined,
      requiresHostApproval: false,
      affectsOtherAttendees: false,
      canChangeHost: true, // Reschedule might assign different host
      notificationScope: 'all',
    };
  }
}

/**
 * Round-robin meeting action handler (rotating hosts)
 * TODO: Implement when round-robin meetings are added
 */
class RoundRobinActionHandler extends BaseActionHandler {
  getActionConstraints(): ActionConstraints {
    return {
      allowReschedule: true,
      allowCancel: true,
      rescheduleDeadlineHours: 24, // Require 24h notice for round-robin
      maxReschedules: 2, // Limit reschedules to prevent host assignment chaos
      requiresHostApproval: false,
      affectsOtherAttendees: false,
      canChangeHost: true, // May need to reassign to different host
      notificationScope: 'all',
    };
  }
}

/**
 * Event booking action handler (multiple independent bookings per slot)
 * Events allow multiple guests to book the same time slot independently
 */
class EventActionHandler extends BaseActionHandler {
  getActionConstraints(): ActionConstraints {
    return {
      allowReschedule: true,
      allowCancel: true,
      rescheduleDeadlineHours: undefined, // No special deadline for events
      maxReschedules: undefined, // No limit on reschedules
      requiresHostApproval: false, // No approval needed (independent bookings)
      affectsOtherAttendees: false, // Independent bookings don't affect others
      canChangeHost: false,
      notificationScope: 'all',
    };
  }
}

/**
 * Main handler factory and utility class
 */
export class MeetingTypeActionHandler {
  private static handlers = new Map<MeetingTypeEnum, BaseActionHandler>([
    ['individual', new IndividualActionHandler()],
    ['collective', new CollectiveActionHandler()],
    ['round_robin', new RoundRobinActionHandler()],
    ['event', new EventActionHandler()],
  ]);
  
  /**
   * Get action constraints for a specific meeting type
   */
  static getActionConstraints(meetingType: MeetingTypeEnum, action: ActionType): ActionConstraints {
    const handler = this.handlers.get(meetingType);
    if (!handler) {
      // Fallback to individual meeting constraints
      return this.handlers.get('individual')!.getActionConstraints();
    }
    return handler.getActionConstraints();
  }
  
  /**
   * Validate if an action is allowed for a specific booking
   */
  static validateAction(
    context: BookingActionContext, 
    action: ActionType
  ): ValidationResult {
    const handler = this.handlers.get(context.meetingType.type);
    if (!handler) {
      // Fallback to individual meeting handler
      return this.handlers.get('individual')!.validateAction(context, action);
    }
    return handler.validateAction(context, action);
  }
  
  /**
   * Get user-friendly message about what an action will do
   */
  static getActionImpactMessage(meetingType: MeetingTypeEnum, action: ActionType): string {
    const handler = this.handlers.get(meetingType);
    if (!handler) {
      return this.handlers.get('individual')!.getActionImpactMessage(action);
    }
    return handler.getActionImpactMessage(action);
  }
  
  /**
   * Check if a meeting type supports a specific action
   */
  static supportsAction(meetingType: MeetingTypeEnum, action: ActionType): boolean {
    const constraints = this.getActionConstraints(meetingType, action);
    return action === 'reschedule' ? constraints.allowReschedule : constraints.allowCancel;
  }
}

export default MeetingTypeActionHandler;